import { ConfidenceLevel, EvidenceType, SourceScope } from '@app/src/types/resourceExplorer'
import { AnswerAcceptanceLevel } from '@app/src/wf-constants'

export const answerAcceptanceLevels = [
  AnswerAcceptanceLevel.Broad,
  AnswerAcceptanceLevel.Balanced,
  AnswerAcceptanceLevel.Strict,
]

export const defaultAnswerAcceptanceLevel = AnswerAcceptanceLevel.Balanced

// Mirrors MaxReportingYearAge of the Strict level in AnswerAcceptance.cs.
const strictMaxReportingYearAge = 2

interface AcceptanceCriterionValue {
  key: string
  strictestAcceptingLevel: AnswerAcceptanceLevel
}

interface AcceptanceCriterion {
  key: 'evidence' | 'source' | 'confidence' | 'reportingYear' | 'figures' | 'documents'
  messagePrefix: string
  messageValues?: Record<string, number>
  values: AcceptanceCriterionValue[]
}

export const acceptanceCriteria = (currentYear: number): AcceptanceCriterion[] => {
  const { Broad, Balanced, Strict } = AnswerAcceptanceLevel

  return [
    {
      key: 'evidence',
      messagePrefix: 'aiScreening.evidence.type',
      values: [
        { key: EvidenceType.ExplicitStatement, strictestAcceptingLevel: Strict },
        { key: EvidenceType.DescribedPractice, strictestAcceptingLevel: Balanced },
        { key: EvidenceType.IndirectHint, strictestAcceptingLevel: Broad },
      ],
    },
    {
      key: 'source',
      messagePrefix: 'aiScreening.evidence.scope',
      values: [
        { key: SourceScope.Entity, strictestAcceptingLevel: Strict },
        { key: SourceScope.ControllingParent, strictestAcceptingLevel: Strict },
        { key: SourceScope.Other, strictestAcceptingLevel: Balanced },
      ],
    },
    {
      key: 'confidence',
      messagePrefix: 'aiScreening.acceptance.aiConfidence',
      values: [
        { key: ConfidenceLevel.High, strictestAcceptingLevel: Strict },
        { key: ConfidenceLevel.Medium, strictestAcceptingLevel: Strict },
        { key: ConfidenceLevel.Low, strictestAcceptingLevel: Broad },
      ],
    },
    {
      key: 'reportingYear',
      messagePrefix: 'aiScreening.acceptance.values',
      messageValues: { year: currentYear - strictMaxReportingYearAge },
      values: [
        { key: 'recentYears', strictestAcceptingLevel: Strict },
        { key: 'anyYear', strictestAcceptingLevel: Balanced },
      ],
    },
    {
      key: 'figures',
      messagePrefix: 'aiScreening.acceptance.values',
      values: [
        { key: 'withReportingYear', strictestAcceptingLevel: Strict },
        { key: 'withoutReportingYear', strictestAcceptingLevel: Broad },
      ],
    },
    {
      key: 'documents',
      messagePrefix: 'aiScreening.acceptance.values',
      values: [
        { key: 'requestedDocument', strictestAcceptingLevel: Strict },
        { key: 'relatedDocument', strictestAcceptingLevel: Broad },
      ],
    },
  ]
}

export const valueMessageIds = (criterion: AcceptanceCriterion, value: AcceptanceCriterionValue) => ({
  label: `${criterion.messagePrefix}.${value.key}`,
  description: `${criterion.messagePrefix}Description.${value.key}`,
})

export const isAcceptedAt = (criterionValue: AcceptanceCriterionValue, level: AnswerAcceptanceLevel): boolean =>
  answerAcceptanceLevels.indexOf(level) <= answerAcceptanceLevels.indexOf(criterionValue.strictestAcceptingLevel)
